import React, { useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import Grid from '../../../components/Grid';
import Tooltip from '../../../components/Tooltip';
import ScholarshipRow from './scholarshipRow';
import messages, { classRankTooltip, gpaTooltip } from './messages';
import findBestMatch from '../../../utils/scholarships';
import getStudentProfileScores from '../../../utils/studentProfiles';
import { getDollarFormattedText } from '../../../utils/strings';
import {
  stateAbbreviationToNameMap,
  sortScholarships,
  getLastUpdatedDate,
  AWARD_RANGE,
  AWARD_FULL_TUITION,
} from '../../../utils/scholarships';
import withHostContext, {
  clickTrackingScholarships,
  trackingLocations,
} from '../../../context/host/consumer';
import './styles.css';

const getToolTip = (msg, classSuffix = '') => (
  <Tooltip
    popupClassName={`financial-aid-tooltip${classSuffix}`}
    contentClassName="financial-aid-tooltip-container"
    contentText={msg}
    showCloseBtn
  >
    <i
      className="info circle icon heading-tooltip-icon"
      tabIndex="0"
      aria-label="Tooltip"
    />
  </Tooltip>
);

const displayColumn = (scholarships, column) =>
  scholarships.some(
    (scholarship) =>
      scholarship[`${column}Min`] !== null || scholarship[`${column}Max`] !== null,
  );
const displayAmountColumn = (session, scid) =>
  !get(session, 'institution') || Number(get(session, 'institution')) === scid;

const getBestMatchAmount = (bestMatch) => {
  /* istanbul ignore if */
  if (bestMatch.awardType === AWARD_FULL_TUITION) return 'Full Tuition';
  return `${getDollarFormattedText(bestMatch.awAmountMin)} ${
    bestMatch.awardType === AWARD_RANGE
      ? '- ' + getDollarFormattedText(bestMatch.awAmountMax)
      : ''
  }`;
};

const sendClickTracking = (studentProfile, hostContext, scid, tracked, setTracked) => {
  if (!tracked) {
    setTracked(true);
    const rank = Number(get(studentProfile, 'classRank', 0));
    const rankPercDelta = rank % 5 > 0 ? 1 : 0;
    const rankPerc = (Math.floor(rank / 5) + rankPercDelta) * 5;

    clickTrackingScholarships(hostContext, {
      location: trackingLocations.TABS.COSTS,
      scids: scid ? [`${scid}`] : [],
      searchGpa: Number(get(studentProfile, 'gpa', 0)),
      searchClassRank: rank ? `top${rankPerc}percent` : '',
      searchSat: Number(get(studentProfile, 'sat', 0)),
      searchAct: Number(get(studentProfile, 'act', 0)),
    });
  }
};

function MeritBasedScholarships({
  scholarships,
  lastUpdated,
  userData,
  applicationStatistics,
  testScores,
  highSchool,
  session,
  scid,
  addInfo,
  hostContext,
}) {
  const [tracked, setTracked] = useState(false);
  const studentProfile = getStudentProfileScores(
    applicationStatistics,
    highSchool,
    userData,
    testScores,
  );
  sendClickTracking(studentProfile, hostContext, scid, tracked, setTracked);
  const bestMatch = findBestMatch(scholarships, studentProfile);
  const lastRowIndex = scholarships.length - 1;
  const sortedScholarships = sortScholarships(scholarships);
  return (
    <React.Fragment>
      {bestMatch !== undefined && (
        <Grid className="best-match-text">
          <Grid.Row>
            <Grid.Column
              computer={8}
              mobile={16}
              className="financial-aid-scholarships-best-match-text"
            >
              <FormattedMessage {...messages.bestMatchText} />
            </Grid.Column>
            <Grid.Column
              computer={7}
              mobile={16}
              className="financial-aid-scholarships-best-match-per-year"
              data-cy="financial-aid-scholarships-best-match-amount"
            >
              {bestMatch.awardType === 'full-tuition' ? (
                <FormattedMessage {...messages.bestMatchFullTuition} />
              ) : (
                <FormattedMessage
                  values={{ amount: getBestMatchAmount(bestMatch) }}
                  {...messages[
                    bestMatch.renewable ? 'bestMatchPerYear' : 'bestMatchOneTime'
                  ]}
                />
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      )}
      <Grid centered columns="equal" className="financial-aid-scholarships-grid">
        <Grid.Row
          key="financial-aid-scholarships-grid-row-0"
          className="financial-aid-scholarships-grid-row first-row"
        >
          <Grid.Column
            textAlign="center"
            className="financial-aid-scholarships-grid-heading-column bold-heading"
          >
            <FormattedMessage {...messages.awardName} />
          </Grid.Column>
          {displayAmountColumn(session, scid) && (
            <Grid.Column
              textAlign="center"
              className="financial-aid-scholarships-grid-heading-column bold-heading scholarships-amount-column"
            >
              <FormattedMessage {...messages.amount} />
            </Grid.Column>
          )}
          {displayColumn(sortedScholarships, 'gpa') && (
            <Grid.Column
              textAlign="center"
              className="financial-aid-scholarships-grid-heading-column bold-heading"
            >
              <FormattedMessage {...messages.gpa} />
              {getToolTip(gpaTooltip, '-gpa')}
            </Grid.Column>
          )}
          {displayColumn(sortedScholarships, 'sat') && (
            <Grid.Column
              textAlign="center"
              className="financial-aid-scholarships-grid-heading-column bold-heading"
            >
              <FormattedMessage {...messages.sat} />
            </Grid.Column>
          )}
          {displayColumn(sortedScholarships, 'act') && (
            <Grid.Column
              textAlign="center"
              className="financial-aid-scholarships-grid-heading-column bold-heading"
            >
              <FormattedMessage {...messages.act} />
            </Grid.Column>
          )}
          {displayColumn(sortedScholarships, 'classRank') && (
            <Grid.Column
              textAlign="center"
              className="financial-aid-scholarships-grid-heading-column bold-heading class-rank-heading"
            >
              <FormattedMessage {...messages.classRank} />
              {getToolTip(classRankTooltip)}
            </Grid.Column>
          )}
        </Grid.Row>
        {sortedScholarships.map((scholarship, index) => (
          <ScholarshipRow
            scholarship={scholarship}
            bestMatch={bestMatch}
            index={index}
            lastRowIndex={lastRowIndex}
            displayGpaColumn={displayColumn(sortedScholarships, 'gpa')}
            displaySatColumn={displayColumn(sortedScholarships, 'sat')}
            displayActColumn={displayColumn(sortedScholarships, 'act')}
            displayClassRankColumn={displayColumn(sortedScholarships, 'classRank')}
            key={`financial-aid-scholarships-grid-row-${index}`}
            displayAmountColumn={displayAmountColumn(session, scid)}
          />
        ))}
      </Grid>
      <div
        className="financial-aid-scholarships-location-info"
        data-cy="financial-aid-scholarships-location-info"
      >
        <FormattedMessage {...messages.location} />
        <b>{stateAbbreviationToNameMap[studentProfile.state]}</b>.
      </div>
      {addInfo && (
        <div className="financial-aid-scholarships-additional-info">
          <b>
            <FormattedMessage {...messages.additionalInfo} />
          </b>
          {addInfo}
        </div>
      )}
      <footer tabIndex={0} className="financial-aid-scholarships-disclaimer">
        <FormattedMessage
          values={{ date: getLastUpdatedDate(lastUpdated) }}
          {...messages.disclaimer}
        />
      </footer>
    </React.Fragment>
  );
}

MeritBasedScholarships.propTypes = {
  applicationStatistics: PropTypes.object,
  scholarships: PropTypes.arrayOf(PropTypes.object),
  lastUpdated: PropTypes.string,
  userData: PropTypes.object,
  intl: PropTypes.object,
  testScores: PropTypes.arrayOf(PropTypes.object),
  highSchool: PropTypes.object,
  session: PropTypes.object,
  scid: PropTypes.number,
  addInfo: PropTypes.string,
  hostContext: PropTypes.object,
};

export default injectIntl(withHostContext(MeritBasedScholarships));
